import { API_BASE_URL, buildApiUrl } from '@/lib/api/base-url';
import { refreshAccessToken, handleAuthFailure } from '@/lib/auth/auth-session';
import { emitAuthEvent } from '@/lib/auth/auth-events';
import { getAccessToken } from '@/lib/auth/token-storage';
import { LOCALE_STORAGE_KEY, defaultLocale } from '@/i18n/config';

function getStoredLocale(): string {
  if (typeof window === 'undefined') return defaultLocale;
  return localStorage.getItem(LOCALE_STORAGE_KEY) ?? defaultLocale;
}

interface ApiFetchOptions {
  skipAuthRetry?: boolean;
  credentials?: RequestCredentials;
}

function createRequest(input: string, init?: RequestInit, options?: ApiFetchOptions): Request {
  const url = API_BASE_URL ? buildApiUrl(input) : input;
  const headers = new Headers(init?.headers ?? undefined);
  const token = getAccessToken();

  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  // Set Accept-Language header based on user's locale preference
  if (!headers.has('Accept-Language')) {
    headers.set('Accept-Language', getStoredLocale());
  }

  const requestInit: RequestInit = {
    ...(init ?? {}),
    headers,
    credentials: options?.credentials ?? init?.credentials ?? 'same-origin',
  };

  return new Request(url, requestInit);
}

async function executeRequest(request: Request): Promise<Response> {
  return fetch(request.clone());
}

function isUnauthorized(status: number): boolean {
  return status === 401;
}

function isForbidden(status: number): boolean {
  return status === 403;
}

export async function apiFetch(
  input: string,
  init?: RequestInit,
  options?: ApiFetchOptions
): Promise<Response> {
  const request = createRequest(input, init, options);
  const response = await executeRequest(request);

  if (isForbidden(response.status)) {
    emitAuthEvent('permission-denied');
    return response;
  }

  if (!isUnauthorized(response.status) || options?.skipAuthRetry) {
    return response;
  }

  // Check if user had a token before attempting refresh
  // This helps distinguish between actual session expiry vs fresh visit with no session
  const hadTokenBeforeRefresh = Boolean(getAccessToken());
  const refreshedToken = await refreshAccessToken({ silent: true });

  if (!refreshedToken) {
    // Only show "session expired" if user had a token (actual session expiry)
    // For fresh visits with no prior session, just redirect without the error message
    handleAuthFailure({
      reason: hadTokenBeforeRefresh ? 'session-expired' : undefined,
      redirectToLogin: true,
      suppressRedirectInfo: !hadTokenBeforeRefresh,
    });
    return response;
  }

  const retryRequest = createRequest(input, init, options);
  const retryResponse = await executeRequest(retryRequest);

  if (isUnauthorized(retryResponse.status)) {
    handleAuthFailure({ reason: 'session-expired', redirectToLogin: true });
  }

  return retryResponse;
}

export function getApiBaseUrl() {
  return API_BASE_URL;
}
